import { useState, useRef, useEffect, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
// @ts-ignore
import DayPicker, { DateUtils } from 'react-day-picker';
import clsx from 'clsx';
import { DayPickerProps } from 'react-day-picker/types/props';
import dayjs from 'dayjs';
import { PaletteUtils, ScrollUtils } from '@shared/utils';
import { DatePickerCaption } from './DatePickerCaption';
import { Button, SelectedDays } from '@shared/widgets';
import DropDownIcon from '@assets/icons/datePicker/dropDown.svg';
import CloseIcon from '@assets/icons/datePicker/close.svg';
import 'react-day-picker/lib/style.css';

const PREVIEW_FORMAT = 'ddd, DD MMM';

interface DatePickerPopupProps {
  className?: string;
  range?: SelectedDays | null;
  disabledDays?: DayPickerProps['disabledDays'];
  onDayClick: (days: SelectedDays) => void;
  onConfirm: (selectedDays: SelectedDays) => void;
  onCancel: () => void;
  isMobile?: boolean;
  ref: React.RefObject<{
    adjustPosition: (rect: DOMRect) => void;
  } | null>;
  open: boolean;
}

interface PreviewColumnProps {
  caption: string;
  value?: string;
}

const PreviewColumn = ({ caption, value }: PreviewColumnProps) => {
  const { t } = useTranslation();
  const displayValue = value ? dayjs.utc(value).format(PREVIEW_FORMAT) : t('datePicker.placeholders.select');

  return (
    <div className='inline-block py-0 px-[5px] w-1/2'>
      <div className='text-[#4c4c52] text-[11px] leading-[3.09] uppercase'>{caption}</div>
      <div
        className={clsx(
          'flex text-[13px] border justify-center items-center h-10 leading-[1] text-center uppercase',
          PaletteUtils.isBordierPalette ? 'border-[#dc291c]' : 'border-[#6d6d75]',
          !!value && (PaletteUtils.isBordierPalette ? 'bg-[#dc291c] border-[#dc291c] text-white' : 'bg-[#6b6b70] border-[#6b6b70] text-[#fcfcfc]'),
        )}
      >
        <span>{displayValue}</span>
      </div>
    </div>
  );
};

export const DatePickerPopup = (props: DatePickerPopupProps) => {
  const [dateRange, setDateRange] = useState<SelectedDays>({ from: props.range?.from, to: props.range?.to });
  const daypickerRef = useRef<DayPicker>(null);
  const nodeRef = useRef<HTMLDivElement>(null);
  const nodeHeaderRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  useEffect(() => {
    if (dateRange.from) daypickerRef.current?.showMonth(dateRange.from as unknown as Date);

    ScrollUtils.scroll.disable();

    return () => {
      ScrollUtils.scroll.enable();
    };
  }, []);

  const handleMonthChange = (value: Date) => daypickerRef.current?.showMonth(value);
  const handleDayClick = (value: Date) => {
    const newRange = DateUtils.addDayToRange(value, dateRange);
    setDateRange(newRange);
    props.onDayClick(newRange);
  };
  const handleConfirmClick = () => props.onConfirm(dateRange);
  const handleHeaderClick = () => {
    if (!props.isMobile) props.onCancel();
  };
  const adjustPosition = (rect: DOMRect) => {
    if (typeof window === 'undefined' || !nodeRef.current) return;

    const popupWidth = nodeRef.current.offsetWidth;
    const viewportWidth = window.innerWidth;
    const scrollX = window.scrollX || window.pageXOffset;
    const scrollY = window.scrollY || window.pageYOffset;

    let left = rect.left + scrollX;

    if (left + popupWidth > viewportWidth + scrollX) {
      left = viewportWidth + scrollX - popupWidth;
    }

    const top = rect.bottom + scrollY - 40;

    nodeRef.current.style.position = 'absolute';
    nodeRef.current.style.left = `${left}px`;
    nodeRef.current.style.top = `${top}px`;
    nodeRef.current.style.width = `${popupWidth}px`;
  };
  useImperativeHandle(props.ref, () => ({
    adjustPosition,
  }));

  const caption = props.isMobile ? t('datePicker.placeholders.selectDateRange') : t('datePicker.placeholders.pickDateRange');

  return (
    <div
      className={clsx(
        'fixed left-0 top-0 h-full w-full bg-black/50 z-[1500]',
        'max-[840px]:fixed max-[840px]:left-0 max-[840px]:top-0 max-[840px]:!z-49',
        'min-[841px]:absolute min-[841px]:h-auto min-[841px]:w-auto',
        props.className,
      )}
      ref={nodeRef}
    >
      <div className='bg-white shadow-[0_2px_14px_0_rgba(0,0,0,0.21)] pb-9 max- mx-auto py-7 px-8 w-[316px]'>
        <div className='text-[#4c4c52] text-[13px] mb-[22px] text-left relative uppercase font-bold' ref={nodeHeaderRef} onClick={handleHeaderClick}>
          {caption}
          <div className={clsx('cursor-pointer h-5 absolute  w-5 ', !props.isMobile ? '-right-3 top-1' : 'right-0 top-0')} onClick={props.onCancel}>
            {props.isMobile ? <CloseIcon /> : <DropDownIcon />}
          </div>
        </div>
        <div className='-mx-[5px] mb-5 text-left font-semibold'>
          <PreviewColumn caption={t('datePicker.placeholders.dateFrom')} value={dateRange.from} />
          <PreviewColumn caption={t('datePicker.placeholders.dateTo')} value={dateRange.to} />
        </div>
        <div>
          <DayPicker
            ref={daypickerRef}
            className='insignia-cards-ui-day-picker'
            numberOfMonths={1}
            //@ts-ignore
            selectedDays={[dateRange.from, { from: dateRange.from, to: dateRange.to }]}
            onDayClick={handleDayClick}
            disabledDays={props.disabledDays}
            firstDayOfWeek={1}
            captionElement={<DatePickerCaption onChange={handleMonthChange} />}
          />
        </div>
        <Button
          className={clsx(
            '!text-[#4c4c52] text-[13px] font-semibold tracking-normal border',
            PaletteUtils.isBordierPalette ? 'border-[#dc291c] bg-white' : 'border-[#be8b45] !bg-[#fcfcfc]',
          )}
          fullWidth
          caption={t('datePicker.buttons.chooseTheseDays')}
          disabled={!dateRange.from || !dateRange.to}
          onClick={handleConfirmClick}
        />
      </div>
    </div>
  );
};
